/**
 * The customer Settings page's one Save, as an ordered list of writes, one per
 * owning module (docs/features/credit-control/06_ui_strategy.md §2, "One Save
 * for the page"). Each owner records its own change, with the one reason, in
 * its own history; nothing links the records, so no transaction spans them.
 *
 * The writes run in the order given and stop at the first refusal: credit
 * control first, so if it refuses nothing else is sent. A later write that
 * fails leaves the earlier ones saved, and the outcome says exactly which.
 * There are two: credit control's payment settings, then the sales ledger's
 * invoicing settings.
 */

/** One owner's part of the Save. */
export interface SettingsWrite {
  /** What the outcome calls it: "Payment methods", "Invoicing settings". */
  part: string;
  write: (reason: string) => Promise<void>;
}

export type SettingsSaveOutcome =
  | { kind: "saved"; saved: string[] }
  | {
      kind: "failed";
      /** The parts written before the refusal: saved, and clean. */
      saved: string[];
      /** The part that was refused; its edits are kept. */
      failed: string;
      error: unknown;
      /** The parts after it, never sent; their edits are kept. */
      notSent: string[];
    };

export async function runSettingsWrites(
  writes: SettingsWrite[],
  reason: string,
): Promise<SettingsSaveOutcome> {
  const saved: string[] = [];
  for (const [i, { part, write }] of writes.entries()) {
    try {
      await write(reason);
    } catch (error) {
      return {
        kind: "failed",
        saved,
        failed: part,
        error,
        notSent: writes.slice(i + 1).map((w) => w.part),
      };
    }
    saved.push(part);
  }
  return { kind: "saved", saved };
}

/**
 * What the save bar says about a failed Save. The refused part shows its own
 * error beside its fields; the bar says what became of the rest:
 * "Payment methods saved. Invoicing settings not saved: …" when an earlier
 * part went through, else that the refused part (and so everything) is
 * unsaved, with the edits kept.
 */
export function saveFailureMessage(
  outcome: Extract<SettingsSaveOutcome, { kind: "failed" }>,
  message: string,
): string {
  if (outcome.saved.length > 0) {
    return `${outcome.saved.join(" and ")} saved. ${outcome.failed} not saved: ${message}`;
  }
  const nothingElse = outcome.notSent.length > 0;
  return `${outcome.failed} not saved${nothingElse ? ", so nothing else was sent" : ""}. Your edits are kept.`;
}
